export const en = {
  meta: {
    appName: 'The Nordic Smart City Circular Fast Track',
    locale: 'en-GB',
  },
  common: {
    close: 'Close',
    next: 'Next',
    previous: 'Previous',
    saveAndClose: 'Save and close',
    edit: 'Edit',
    share: 'Share',
    shared: 'Shared',
    openInNewWindow: 'Open in new window',
    notProvided: 'Not provided',
    unknownDate: 'Unknown date',
  },
  header: {
    logoAlt: 'Copenhagen municipality',
    osloLabel: 'Copenhagen',
    searchPlaceholder: 'Search the entire website',
    searchAriaLabel: 'Search the entire website',
    searchButtonAriaLabel: 'Search',
    menu: 'Menu',
    contact: 'Contact',
    application: 'Application',
    dashboard: 'Dashboard',
  },
  stepper: {
    ariaLabel: 'Application steps',
    municipalityOwner: 'Municipality',
    partnerOwner: 'Partner',
  },
  stepNavigation: {
    previous: 'Previous',
    next: 'Next',
    submit: 'Submit application',
  },
  applicationSteps: {
    1: 'About the project',
    2: 'Support & attachments',
    3: 'Trust proofs',
    4: 'Summary',
  },
  roleSwitcher: {
    actingAs: 'You are acting as',
    municipalityName: 'Mikkel Sørensen',
    partnerFallback: 'GreenLoop Labs',
    wallet: 'Wallet',
    walletSwitchAriaLabel: 'Switch between Symfoni and mock wallet',
    resetDemo: 'Reset demo',
    switchToPartner: 'Switch to {partnerName}',
    switchToMunicipality: 'Switch to {municipalityName}',
  },
  englishPlaceholder: {
    title: 'The Nordic Smart City Circular Fast Track (English)',
    body: 'English variant scaffold is ready for task 4-7.',
  },
  dashboard: {
    title: 'Supplier overview',
    statsTotalLabel: 'Total count',
    statsApprovedLabel: 'Approved (low risk)',
    statsApprovedSubLabel: 'Score > 75%',
    statsFollowupLabel: 'Needs follow-up',
    statsFollowupSubLabel: 'Score 50-74%',
    statsRiskLabel: 'High risk / deviation',
    statsRiskSubLabel: 'Action required',
    liveMonitoring: 'Live monitoring active',
  },
  statusBadge: {
    ok: 'OK',
    review: 'Review details',
    critical: 'Critical',
  },
  projectTable: {
    empty: 'No projects found',
    project: 'Project',
    partner: 'Partner',
    riskScore: 'Real-time risk score',
    status: 'Status',
    detailsAria: 'Show details for {projectName}',
  },
  riskChart: {
    title: 'Risk distribution - real time',
    ariaLabel: 'Risk distribution: {ok} approved, {review} needs follow-up, {critical} critical',
    averageTrust: 'Average trust',
    noDeviation: 'No deviations',
    reviewDetails: 'Notice: review details',
    criticalStop: 'Critical / stop',
  },
  projectDetail: {
    totalRiskScore: 'Total risk score',
    atomicCredentials: 'Atomic credentials (micro proofs)',
    realtimeObject: 'Realtime object',
    dynamicPolicyLogic: 'Dynamic policy logic',
    totalCalculated: 'Total calculated',
    simulatorTitle: 'Simulator (continuous vetting)',
    simulatorDescription:
      'Simulate an event from an issuer (for example the tax authority) to see how the risk score updates instantly.',
    simulatorAction: 'SIMULATE:',
    simulatorChangeStatus: '(Change status)',
    simulatorSelectHint:
      'Click a proof on the left to select it, then click SIMULATE to change status.',
    applicationDate: 'Application date: {date}',
    updatedDate: 'Last updated: {date}',
    block: '(BLOCK)',
    ok: '(OK)',
  },
  modalCredentialCard: {
    issuer: 'Issuer: {issuer} • {date}',
    lastEventManualRevoked: 'Last event: Manually revoked (simulation)',
    lastEvent: 'Last event: {reason}',
    lastEventInvalid: 'Last event: Proof invalid',
    validStatus: 'VALID / OK',
    invalidStatus: 'INVALID / REVOKED',
  },
  circularProgress: {
    scoreAriaLabel: '{percentage}% score',
  },
  partnerInbox: {
    title: 'New request from Copenhagen municipality',
    intro:
      'Copenhagen municipality has started an application for The Nordic Smart City Circular Fast Track with {partnerName} (org.no {orgnr}) as partner.',
    projectTitle: 'Project title: {title}',
    whatToDo: 'What do you need to do?',
    task1: 'Fill in support details and upload attachments',
    task2: 'Share trust proofs from your business wallet',
    task3: 'Send back to Mikkel Sørensen for review',
    start: 'Start filling in',
    partnerFallback: 'GreenLoop Labs',
    orgFallback: 'Not provided',
    projectFallback: '(Not provided)',
  },
  step1: {
    loginMethodTitle: 'Choose sign-in method',
    walletAlt: 'Digital wallet',
    walletTitle: 'Digital wallet',
    walletSubtitle: 'Use your digital wallet',
    processingLogin: 'Processing shared data...',
    entraAlt: 'Microsoft Entra ID',
    entraTitle: 'Microsoft Entra',
    entraSubtitle: 'Organization sign-in',
    loggedInAs: 'Signed in as: {name}',
    title: 'The Nordic Smart City Circular Fast Track grant scheme',
    infoText:
      'Here you can apply for support to bring a pilot from one Nordic network city into another city that wants to test it locally. Be aware that this application may become publicly available after submission. Therefore, do not include market-sensitive information or business ideas that cannot be publicly shared.',
    applyingAs: 'You are applying as a representative of {city}.',
    applyForAnotherLabel: 'Which city should receive this pilot?',
    applyForAnotherHint:
      'This will usually be {city}, but you can change it here if another city should receive the pilot.',
    selectUnitPlaceholder: 'Select city',
    projectTitleLabel: 'Project title',
    projectTitlePlaceholder: 'Use a temporary name if the project does not have a final name yet',
    descriptionLabel: 'Which pilot from another city do you want to bring in?',
    descriptionPlaceholder:
      'Briefly describe the pilot, where it has already been tested (for example Oslo), and how you will adapt it locally',
    problemLabel: 'Which local challenge will this imported pilot solve?',
    problemPlaceholder: 'Describe the challenge in the receiving city that the pilot will address',
    areasLabel: 'In which area will the project have impact?',
    partnerSectionLabel: 'Partner',
    partnerSectionHint: 'Select the partner country and identifier to verify the company in eAidRegister.',
    partnerAdded: 'Partner added',
    partnerName: 'Name: {name}',
    partnerCountry: 'Country: {country}',
    partnerOrgnr: 'Identifier: {orgnr}',
    partnerCountryLabel: 'Partner country',
    partnerCountryPlaceholder: 'Select country',
    partnerCountryHint: 'Choose the country where the partner is registered in eAidRegister.',
    partnerIdentifierPlaceholder: 'Partner identifier in eAidRegister',
    partnerIdentifierHint: 'Use the partner identifier registered in eAidRegister. Country-specific formats vary.',
    orgnrPlaceholder: '9-digit organization number',
    orgnrHint: 'Organization number must be exactly 9 digits',
    fetchingBrreg: 'Fetching organization name from Brreg...',
    sentToPartnerTitle: 'Sent to partner',
    sentToPartnerDescription:
      'Waiting for partner to fill in trust proofs and support information.',
    sendToPartner: 'Send to partner',
    brregError: 'Could not fetch organization name from Brreg',
    fallbackPartnerName: 'Partner {orgnr}',
    eAidCountriesError: 'Could not load eAidRegister countries',
  },
  step2: {
    title: 'Support & attachments',
    waitingTitle: 'Waiting for partner',
    waitingBody:
      'This part of the application should be completed by partner {partnerName}{orgSuffix}. You can see the content when the partner has completed their part and returned the application.',
    attachmentsLabel: 'Attachments',
    completedTitle: 'Your part is complete',
    completedBody: 'Switch to Mikkel Sørensen to review and complete the application.',
  },
  step3: {
    title: 'Trust proofs',
    fallbackError: 'Something went wrong while fetching from the business wallet.',
    failureFallback: 'Sharing from business wallet was rejected or failed.',
    cancelled: 'Cancelled',
    invalidExpiry: 'Received invalid expiry time from the business wallet.',
    timeout: 'Sharing took too long. Cancel and try again.',
    expired: 'The request expired before sharing was completed. Try again.',
    selectAtLeastOne: 'Select at least one proof before fetching from business wallet.',
    noSharedProofs: 'No recognized proofs were shared back from business wallet.',
    creatingRequest: 'Creating request...',
    simulatingShare: 'Simulating share...',
    waitingForShare: 'Waiting for share...',
    fetchFromWallet: 'Fetch from business wallet',
    showMockWallet: 'Show mock wallet',
    fetchFromMockWallet: 'Fetch from mock wallet',
    cancel: 'Cancel',
    hintSymfoni:
      'Select one or more proofs, click Fetch from business wallet, and complete sharing in the Symfoni app.',
    hintMock:
      'Click Fetch from mock wallet to open popup and share proofs one by one. The application updates continuously.',
    hintReadonly:
      'All available proofs are shown below. Sharing is performed by the partner in the business wallet.',
    waitingPartnerTitle: 'Waiting for partner',
    waitingPartnerBody:
      'When the partner has shared proofs from the business wallet, score and status are updated automatically.',
    popupBlockedTitle: 'Popup was blocked',
    popupBlockedBody: 'Allow popups in your browser to use the mock wallet.',
    waitingConsentTitle: 'Waiting for consent in Symfoni',
    waitingConsentBody:
      'Open the wallet, select Share, and return here. We check status automatically every 1-2 seconds.',
    requestId: 'Request ID: {id}',
    manualOpenTitle: 'Could not open wallet automatically',
    manualOpenBody: 'The browser blocked a new window. Open the link manually to complete sharing:',
    openSymfoniRequest: 'Open Symfoni request',
    walletErrorTitle: 'Could not fetch proofs',
    receivedFrom: 'Proofs received from {source}',
    lastUpdated: 'Last updated: {date}',
    noProofsReceived: 'No proofs received',
    noProofsSharedYet: 'No proofs shared yet',
    noProofsReceivedBody: 'Partner has not shared any proofs from the wallet yet.',
    noProofsSharedYetBody:
      'Open the mock wallet and share proofs one by one. Overview updates continuously.',
    selectedProofs: 'Selected proofs',
    selectedCount: '{count} selected',
    selectedNone: 'None selected yet',
    sharedCount: '{count} shared',
    sharedNone: 'None shared yet',
    partnerLookupTitle: 'Partner matched from eAidRegister',
    orgnrLabel: 'Identifier:',
    nameLabel: 'Name:',
    contactLabel: 'Contact:',
    emailLabel: 'Email:',
    completedTitle: 'Your part is complete',
    completedBody: 'Switch to Mikkel Sørensen to review and complete the application.',
    sendBack: 'Complete and send back',
    sentFromWalletLabel: 'business wallet',
    sentFromMockWalletLabel: 'mock wallet',
  },
  step4: {
    submittedTitle: 'Application sent!',
    submittedBody:
      'Your application has been received and will be processed. You will receive a confirmation by email.',
    referenceNumber: 'Reference number: {reference}',
    title: 'Summary & self-declaration',
    projectInfo: 'Project information',
    projectName: 'Project name:',
    projectCity: 'Receiving city:',
    company: 'Organization:',
    areas: 'Areas:',
    requestedGrant: 'Requested grant from The Nordic Smart City Circular Fast Track:',
    partner: 'Partner',
    partnerOrg: 'Identifier:',
    contactPerson: 'Contact person:',
    support: 'Support',
    deMinimisExceeded: 'De minimis support may be exceeded',
    supportCheckRequired: 'The support check could not complete. Try again before submitting.',
    retrySupportCheck: 'Retry support check',
    deMinimisQualified: 'Partner is eligible for de minimis support',
    previousSupport: 'Previous support (3 years):',
    totalWithGrant: 'Total with grant:',
    limit: 'Limit:',
    agreementSigning: 'Signing the collaboration agreement',
    signedByPartner: 'Signed by {partnerName} (identifier {orgnr})',
    partnerNotSigned: 'Partner has not signed the collaboration agreement yet',
    warningTitle: 'The collaboration agreement is not signed by Copenhagen municipality',
    warningBody:
      'You can still submit the application, but we recommend signing the agreement first.',
    goBackAndSign: 'Go back and sign',
    submitAnyway: 'Submit anyway',
    budgetAndCosts: 'Budget and cost plan',
    declarationsTitle: 'Self-declaration – Copenhagen municipality',
    submitApplication: 'Submit application',
    municipalityAgreementFileContext: 'Copenhagen municipality',
    partnerAgreementFileContext: 'the partner',
  },
  walletPopup: {
    title: 'Symfoni business wallet',
    subtitle: 'Digital business wallet',
    intro: 'Choose which proofs to share with the application. Click "Share" to send the proof.',
    points: '{points} pts',
    footer: 'Symfoni business wallet',
  },
  personalWalletPopup: {
    title: 'Symfoni ID',
    subtitle: 'Personal wallet',
    intro: 'Share your employee certificate from Copenhagen Municipality to sign in.',
    cardTitle: 'Employee certificate',
    cardType: 'Municipal employee certificate',
    name: 'Name:',
    department: 'Department:',
    section: 'Section:',
    employeeNumber: 'Employee no:',
    issuedBy: 'Issued by: Copenhagen Municipality',
    issuedAtLabel: 'Issued:',
    issuedAtValue: '15.08.2023',
    footer: 'Symfoni ID personal wallet',
  },
  signingPopup: {
    title: 'Sign document',
    subtitle: 'Digital business wallet',
    step1Body: 'You have received a document for signing.',
    fileAlt: 'Word document',
    fileName: 'CollaborationAgreement.docx',
    fileType: 'Microsoft Word document',
    viewDocument: 'View document',
    signBody: 'You are signing the document digitally with your business wallet.',
    signDocument: 'Sign document',
    signed: 'The document has been signed',
    contextLabel: 'the partner',
  },
  kommuneSigningPopup: {
    title: 'Sign document',
    subtitle: 'Digital wallet – Copenhagen municipality',
    step1Body: 'You have received a document for countersigning.',
    fileAlt: 'Word document',
    fileName: 'CollaborationAgreement.docx',
    fileType: 'Microsoft Word document',
    viewDocument: 'View document',
    partnerSigned: 'Already signed by partner',
    signBody: 'You are signing on behalf of Copenhagen municipality (org.no 958 935 420).',
    signDocument: 'Sign document',
    signed: 'The document has been signed',
    contextLabel: 'Copenhagen municipality',
  },
  agreementDocumentDialog: {
    title: 'Collaboration agreement between organization and beneficiary',
    fileName: 'Avtale-mellom-virksomhet-og-stottemottaker-Innovasjonsprosjekt.pdf',
    dialogTitle: 'View collaboration agreement',
    descriptionWithContext: 'This is the agreement that will be signed by {contextLabel}.',
    descriptionDefault: 'This is the agreement that will be signed.',
    fallbackInfo: 'If the document is not shown, you can open it in a new window.',
  },
  agreementSigning: {
    documentName: 'CollaborationAgreement.docx',
    signedAt: 'Signed {timestamp}',
    sectionLabel: 'Collaboration agreement',
    signButton: 'Sign collaboration agreement',
    fileAlt: 'Word document',
  },
  budgetButton: {
    title: 'Budget and cost plan',
    fileAlt: 'Budget',
    totalCost: 'Total cost: {amount}',
    balanceOk: ' · Balance: OK',
    balanceDeviation: ' · Balance: Deviation',
    filledAt: ' · Filled in {timestamp}',
    fillButton: 'Fill in budget',
  },
  budgetDialog: {
    title: 'Budget and cost plan',
    partnerDescription: 'Fill in project costs and/or own contribution for your organization.',
    municipalityDescription: 'Fill in financing plan and project costs for the municipality.',
    financingPlan: 'Financing plan',
    contributionSummary: 'Calculated own contribution',
    post: 'Item',
    amountNok: 'Amount (NOK)',
    percentOfTotal: '% of total',
    totalCost: 'Total cost',
    totalFinancing: 'Total financing',
    partnerContribution: 'Own contribution from partner',
    municipalityContribution: 'Own contribution from municipal organization',
    totalContribution: 'Total own contribution',
    partnerContributionIntro:
      'This project requires at least a 50% own contribution. Enter your project expenses here so your contribution can be calculated.',
    municipalityContributionIntro:
      "This project requires at least a 50% own contribution. Enter the municipality's project expenses here so its contribution can be calculated.",
    partnerTotalExpenses: 'Your total project expenses',
    municipalityTotalExpenses: "The municipality's total project expenses",
    partnerContributionSummaryText:
      "These are your total expenses in the project. They count as your contribution together with the municipality's share, to meet the requirement of at least a 50% own contribution.",
    municipalityContributionSummaryText:
      "These are the municipality's total expenses in the project. They count as the municipality's contribution together with the partner's share, to meet the requirement of at least a 50% own contribution.",
    requestedGrant: 'Requested grant from The Nordic Smart City Circular Fast Track',
    amountLockedHint: 'The amount is locked for the municipality in summary.',
    contributionThresholdError: 'Own contribution must be at least 50% of total financing',
    applicantProjectCosts: 'Applicant project costs (municipal organization)',
    partnerProjectCosts: 'Partner project costs',
    projectResult: 'Project result',
    totalFinancingLabel: 'Total financing:',
    totalCostsLabel: 'Total costs:',
    differenceLabel: 'Difference:',
    balanced: 'Budget is balanced',
    unbalanced: 'Budget is not balanced (difference: {amount})',
  },
  deMinimis: {
    checking: 'Checking support register...',
    qualifiedUpTo: 'Your partner is eligible for de minimis support up to {amount}',
    noHitsAssumption:
      'The support register has no matches now, and we assume the partner can receive the amount.',
    verificationUnavailable: 'Automatic eligibility check is unavailable',
    verificationUnavailableBody:
      'We could not verify the partner in the support register right now. Open eAidRegister to confirm the partner before proceeding.',
    mayExceed: 'De minimis support may be exceeded',
    exceedBody:
      'With a grant of {requested}, total support will exceed the limit of {limit} in a rolling 3-year period.',
    previousSupport: 'Previously granted support (last 3 years):',
    plannedSupport: 'Planned support (fixed):',
    sum: 'Sum:',
    limit: 'Limit:',
    basedOnHistory: 'Based on registered grants in the last 3 years.',
    eAidRegisterHint: 'Search for organization number {orgnr} in the EU eAidRegister for a live verification.',
    openEaidRegister: 'Open eAidRegister',
  },
  eAidDeMinimis: {
    checking: 'Checking eAidRegister...',
    eligible: 'Partner appears eligible for additional de minimis support',
    mayExceed: 'Partner may exceed the EU de minimis ceiling',
    basedOnRegister: 'Based on de minimis awards registered in eAidRegister during the last 3 years.',
    noRecordedAidBody: 'No de minimis awards were found in eAidRegister for this identifier in the last 3 years.',
    verificationUnavailable: 'Could not verify against eAidRegister',
    verificationUnavailableBody:
      'The EU register could not be queried right now. Open eAidRegister to verify the partner manually.',
    matchedPartner: 'Matched partner:',
    country: 'Country:',
    identifier: 'Identifier:',
    registeredAid: 'Registered aid last 3 years:',
    plannedAidApprox: 'Planned support (approx.):',
    sum: 'Sum:',
    limit: 'EU limit:',
    openRegister: 'Open eAidRegister',
  },
  trustScorePanel: {
    title: 'Total Trust Score',
    updatedAt: 'Updated: {time}',
    approved: 'Approved',
    thresholdLabel: 'Contract threshold:',
    points: '{points} Points',
    sliderHint: 'You set the requirements. Adjust the slider.',
    howItWorksTitle: 'How it works',
    howItWorksParagraph1:
      'The trust score is based on a negative trust model: instead of looking for reasons to trust the applicant, we verify that there are no warning signs in public registers.',
    howItWorksParagraph2:
      'Each valid verification gives points. The score updates automatically. If a verification falls away, for example due to missing tax payment, the score drops immediately.',
    pointsLabel: 'Points',
  },
  trustScoreDisplay: {
    compactOk: 'OK',
    compactBelow: 'Below threshold',
    title: 'Trust score',
    points: 'points',
    progressAriaLabel: 'Trust score',
    thresholdLabel: 'Threshold: {threshold}',
    approvedStatus: 'APPROVED - Score above threshold',
    belowStatus: 'BELOW THRESHOLD - Needs follow-up',
  },
  credentialPanel: {
    points: '{points} pts',
    sharedFrom: 'Shared from {source}',
    required: 'Required',
    notSharedYet: 'Not shared yet',
  },
  fileUploadButton: {
    upload: 'Upload',
    inactiveDemo: '(Not active in demo)',
  },
  domain: {
    costLabels: {
      lonn: 'Salary',
      eksterneTjenester: 'External services',
      utstyrOgMaterialer: 'Equipment and materials',
      reiseOgMoter: 'Travel and meetings',
      kommunikasjon: 'Communication',
      andreUtgifter: 'Other expenses',
    },
    sourceIssuerLabels: {
      oid: 'Organization',
      brreg: 'Brreg',
      skatteetaten: 'Tax authority',
      bank: 'Bank & Finance',
      tilsyn: 'Supervisory authority',
    },
    sourceCategoryLabels: {
      oid: 'Identification',
      brreg: 'Company registration',
      skatteetaten: 'Tax certificate',
      bank: 'Credit rating',
      tilsyn: 'HSE/Supervision',
    },
    declarations: [
      'I understand that this application may become publicly available afterwards.',
      'I confirm that no trade secrets or other sensitive information that cannot be published are included.',
      'I have read and understood the guidelines for using The Nordic Smart City Circular Fast Track funds.',
    ],
  },
} as const;

export type EnMessages = typeof en;
